/**
 * 프로젝트 허브 목록 뷰 — 아웃라이너식 WBS 편집.
 * - 제목 클릭 → 인라인 편집. Enter 는 저장 후 아래에 새 형제 작업(연속 입력),
 *   Shift+Enter 저장만, Esc 취소, Tab/Shift+Tab 들여쓰기/내어쓰기(상위 변경).
 * - 행 포커스 상태에서 ↑↓ 이동, Enter 편집, Space 완료, Delete 삭제,
 *   Tab/Shift+Tab 계층, Alt+↑↓ 순서, Alt+←→ 날짜 ±1일, Ctrl+Enter 패널 열기,
 *   Ctrl+A 전체 선택, Ctrl+D 복제.
 * - Ctrl/⌘/Shift+클릭 다중 선택 → 하단 액션 바(완료·색상·±1일·기간·계층·복제·삭제).
 * - 여러 줄 붙여넣기 / 일괄 입력창: 한 줄에 하나, 들여쓰기가 하위 작업.
 * - 하위 작업 접기/펼치기 (localStorage 에 기억).
 * 모든 변경은 히스토리 스택(Ctrl+Z)과 #projHub 소프트 갱신을 거친다.
 */
import { api, createCommand, deleteCommand, patchCommand, composite, commit, commitQuiet, record, recordQuiet, showToast, labels, fmtCount, beginDrag, endDrag, refreshGrid } from './calendar-history.js';
import { attachDatePicker, syncDateLabel } from './datepicker.js';
import { showContextMenu } from './context-menu.js';
import { navigate } from './calendar-spa.js';
import { initInappSelects } from './inapp-select.js';
import { openPillPicker, pill } from './prop-picker.js';
import { openPeoplePicker } from './people-picker.js';
import { openEventPicker } from './event-picker.js';
import { bindQuickTime } from './quick-time.js';
import { splitScope, sendScoped, deleteScoped } from './calendar-scope.js';

function localISODate(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}
function addDays(ds, n) {
    const d = new Date(ds + 'T00:00:00');
    d.setDate(d.getDate() + n);
    return localISODate(d);
}
function inTextField(t) {
    if (!t) return false;
    const tag = (t.tagName || '').toUpperCase();
    if (tag === 'INPUT') return !/^(checkbox|radio|button|submit|reset|range|color|file)$/i.test(t.type || 'text');
    return tag === 'TEXTAREA' || tag === 'SELECT' || !!t.isContentEditable;
}
function overlayOpen() {
    const m = document.getElementById('calendarModal');
    const p = document.getElementById('calProjModal');
    return (m && m.style.display === 'flex') || (p && p.style.display === 'flex');
}

// "  설계\n    화면 정의" 같은 텍스트를 {depth, title} 목록으로. 공백 2칸 또는 탭 = 1단계,
// 앞의 "- ", "* ", "1. " 는 떼어낸다. 깊이는 바로 위 줄 +1 까지만 허용.
export function parseOutline(text) {
    const out = [];
    let prev = -1;
    String(text || '').split(/\r?\n/).forEach((raw) => {
        if (!raw.trim()) return;
        const m = raw.match(/^([ \t]*)(.*)$/);
        const lead = m[1].replace(/\t/g, '  ').length;
        let depth = Math.floor(lead / 2);
        const title = m[2].replace(/^(?:[-*•]|\d+[.)])\s+/, '').trim();
        if (!title) return;
        if (depth > prev + 1) depth = prev + 1;
        out.push({ depth, title });
        prev = depth;
    });
    return out;
}

export function setupProjectList() {
    const hub = document.getElementById('projHub');
    if (!hub || hub.dataset.spaBound) return; // 뷰 전환으로 되살아난 루트는 이미 묶여 있다
    hub.dataset.spaBound = '1';
    const calId = hub.dataset.calId;
    const calColor = hub.dataset.calColor || 'c1';
    const today = hub.dataset.today || localISODate(new Date());
    const selBar = document.getElementById('calpSelBar');
    const selCount = document.getElementById('calpSelCount');
    const foldKey = 'calp-fold:' + calId;

    // ── 행 조회 ──
    const rows = () => Array.from(hub.querySelectorAll('.calp-tr[data-id]'));
    const rowById = (id) => hub.querySelector('.calp-tr[data-id="' + CSS.escape(id) + '"]');
    const num = (r) => parseFloat(r.dataset.order || '0') || 0;
    const siblings = (parent) => rows().filter((r) => (r.dataset.parent || '') === (parent || ''));
    const children = (id) => siblings(id);
    function descendants(id) {
        const out = [];
        const walk = (pid) => children(pid).forEach((ch) => { out.push(ch); walk(ch.dataset.id); });
        walk(id);
        return out;
    }
    function prevSibling(row) {
        const sib = siblings(row.dataset.parent);
        const i = sib.indexOf(row);
        return i > 0 ? sib[i - 1] : null;
    }
    function nextSibling(row) {
        const sib = siblings(row.dataset.parent);
        const i = sib.indexOf(row);
        return i >= 0 && i < sib.length - 1 ? sib[i + 1] : null;
    }
    const orderAfter = (row) => { const n = nextSibling(row); return n ? (num(row) + num(n)) / 2 : num(row) + 1; };
    const orderBefore = (row) => { const p = prevSibling(row); return p ? (num(p) + num(row)) / 2 : num(row) - 1; };
    const maxOrder = () => rows().reduce((m, r) => Math.max(m, num(r)), 0);
    const visibleRows = () => rows().filter((r) => !r.hidden);

    // ── 선택 ──
    const selected = new Set();
    let anchorId = null;
    function applySelection() {
        rows().forEach((r) => {
            const on = selected.has(r.dataset.id);
            r.classList.toggle('selected', on);
            const cb = r.querySelector('.calp-sel');
            if (cb) cb.checked = on;
        });
        hub.classList.toggle('has-selection', selected.size > 0);
        Array.from(selected).forEach((id) => { if (!rowById(id)) selected.delete(id); });
        if (selBar) {
            selBar.hidden = selected.size === 0;
            if (selCount) selCount.textContent = fmtCount(labels().selected, selected.size);
        }
        const all = document.getElementById('calpSelAll');
        if (all) {
            const n = rows().length;
            all.checked = n > 0 && selected.size === n;
            all.indeterminate = selected.size > 0 && selected.size < n;
        }
        if (selected.size === 0) hideDatesPop();
    }
    function clearSelection() {
        selected.clear();
        applySelection();
        const bp = document.getElementById('calpBulkProp');
        if (bp) bp.remove();
    }
    function selectRange(a, b) {
        const list = visibleRows();
        const ia = list.findIndex((r) => r.dataset.id === a), ib = list.findIndex((r) => r.dataset.id === b);
        if (ia < 0 || ib < 0) return;
        for (let i = Math.min(ia, ib); i <= Math.max(ia, ib); i += 1) selected.add(list[i].dataset.id);
    }
    // 작업 대상: 선택이 있으면 선택, 없으면 포커스된 행
    function targets() {
        if (selected.size) return Array.from(selected).map(rowById).filter(Boolean);
        const f = focusedRow();
        return f ? [f] : [];
    }

    // ── 포커스 ──
    let focusedId = null;
    function focusedRow() {
        const active = document.activeElement && document.activeElement.closest ? document.activeElement.closest('.calp-tr[data-id]') : null;
        return active || (focusedId ? rowById(focusedId) : null);
    }
    hub.addEventListener('focusin', (e) => {
        const r = e.target.closest('.calp-tr[data-id]');
        if (r) focusedId = r.dataset.id;
    });
    function focusRow(row) {
        if (!row) return;
        focusedId = row.dataset.id;
        row.focus({ preventScroll: false });
    }

    // ── 접기 ──
    let folded = new Set();
    try { folded = new Set(JSON.parse(localStorage.getItem(foldKey) || '[]')); } catch (_) { folded = new Set(); }
    function saveFolded() {
        try { localStorage.setItem(foldKey, JSON.stringify(Array.from(folded))); } catch (_) { /* 무시 */ }
    }
    function applyFolded() {
        rows().forEach((r) => {
            let hidden = false;
            let pid = r.dataset.parent || '';
            let guard = 0;
            while (pid && guard < 50) {
                if (folded.has(pid)) { hidden = true; break; }
                const p = rowById(pid);
                pid = p ? (p.dataset.parent || '') : '';
                guard += 1;
            }
            r.hidden = hidden;
            const btn = r.querySelector('.calp-fold');
            if (btn) btn.classList.toggle('is-folded', folded.has(r.dataset.id));
        });
    }
    function toggleFold(id) {
        if (folded.has(id)) folded.delete(id);
        else folded.add(id);
        saveFolded();
        applyFolded();
    }

    document.addEventListener('calendar:refreshed', () => {
        applyFolded();
        applySelection();
        if (focusedId && rowById(focusedId) && !inTextField(document.activeElement)) rowById(focusedId).focus({ preventScroll: true });
    });
    applyFolded();

    // ── 생성 헬퍼 ──
    function createBody(title, start, end, parent, order, extra) {
        const b = new URLSearchParams();
        b.set('title', title);
        b.set('start_date', start);
        b.set('end_date', end);
        b.set('all_day', 'on');
        b.set('color', (extra && extra.color) || calColor);
        b.set('calendar_id', calId);
        b.set('parent_id', parent || '');
        b.set('sort_order', String(order));
        return b;
    }
    // 그룹 머리줄 「＋」 의 프리필: color 는 생성 body 에, 나머지(prop:<id>·done)는 생성 뒤 patch
    function applyPrefill(ev, prefill) {
        const rest = Object.assign({}, prefill || {});
        delete rest.color;
        return Object.keys(rest).length ? api.patch(ev.id, rest).then(() => ev) : Promise.resolve(ev);
    }
    function fail() {
        showToast(labels().failed, { error: true });
    }

    // ── 인라인 제목 편집 ──
    function startEdit(row) {
        if (row.querySelector('input.calp-edit')) return;
        const textEl = row.querySelector('.calp-td-text');
        if (!textEl) return;
        const old = row.dataset.title;
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'calp-edit';
        input.maxLength = 200;
        input.value = old;
        textEl.replaceWith(input);
        input.focus();
        input.select();
        let finished = false;
        function finish(save, after) {
            if (finished) return;
            finished = true;
            const val = input.value.trim();
            const span = document.createElement('span');
            span.className = 'calp-td-text';
            span.textContent = save && val ? val : old;
            input.replaceWith(span);
            const id = row.dataset.id;
            const changed = save && val && val !== old;
            // 반복 인스턴스의 제목은 범위를 묻는다 (BA1) — 취소하면 옛 제목으로
            const p = !changed ? Promise.resolve() : splitScope([row], 'edit').then((sp) => {
                if (!sp) { span.textContent = old; return null; }
                if (sp.scoped.length) return sendScoped(sp.scoped, sp.scope, () => ({ title: val }));
                row.dataset.title = val; // 낙관적 반영
                const bar = row.querySelector('.calp-bar[data-eid]');
                if (bar) bar.title = val;
                return commitQuiet(patchCommand(id, { title: old }, { title: val }));
            });
            p.then(() => {
                if (after === 'new') createSiblingAfter(id, false);
                else if (after === 'indent') indent([rowById(id)].filter(Boolean));
                else if (after === 'outdent') outdent([rowById(id)].filter(Boolean));
                else focusRow(rowById(id));
            });
        }
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                finish(true, e.shiftKey || e.ctrlKey || e.metaKey ? null : 'new');
            } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                finish(false);
            } else if (e.key === 'Tab') {
                e.preventDefault();
                finish(true, e.shiftKey ? 'outdent' : 'indent');
            }
        });
        input.addEventListener('blur', () => finish(true));
    }

    // 임시 행: 서버에 저장되기 전의 새 작업 입력 줄. Enter → 생성(+다음 줄), Esc/빈 blur → 제거.
    function createSiblingAfter(afterId, asChild, afterEl, prefill) {
        const after = afterEl || rowById(afterId);
        const parent = after ? (asChild ? after.dataset.id : (after.dataset.parent || '')) : '';
        const order = after ? (asChild ? (children(after.dataset.id).length ? maxOrderOf(children(after.dataset.id)) + 1 : num(after) + 0.5) : orderAfter(after)) : maxOrder() + 1;
        const start = ''; // 새 작업은 날짜 미정으로 시작한다
        const end = '';
        const depth = after ? (parseInt(after.dataset.depth || '0', 10) + (asChild ? 1 : 0)) : 0;
        const temp = document.createElement('div');
        temp.className = 'calp-tr calp-row calp-temp';
        // 뷰의 열 수만큼 빈 칸 — 서버 갱신이 진짜 셀(기간 미니 트랙 등)로 채운다
        temp.innerHTML = '<span class="calp-td-check"></span><span class="calp-td-title" style="padding-left:' + (depth * 18) + 'px;"><span class="calp-fold-spacer"></span><span class="calp-swatch cal-' + calColor + '"></span></span>' + '<span></span>'.repeat(Math.max(1, parseInt(hub.dataset.colCount || '3', 10) || 3));
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'calp-edit';
        input.maxLength = 200;
        input.placeholder = hub.dataset.quickPh || '';
        temp.querySelector('.calp-td-title').appendChild(input);
        // 날짜 감지 칩 (AZ2): 날짜·반복이 보일 때만 — 그러면 그 날짜의 작업(반복이면 시리즈)이 된다
        const chipEl = document.createElement('span');
        temp.querySelector('.calp-td-title').appendChild(chipEl);
        const H = hub.dataset;
        const qt = bindQuickTime(input, chipEl, { offLabel: H.quickTimeOff, today: H.today || '', needDate: true, weekdays: H.weekdays, fromFmt: H.quickFrom, weekStart: H.weekStart });
        // 위치: after 와 그 자손들 바로 뒤
        let anchor = after;
        if (after) {
            const desc = after.dataset.id ? descendants(after.dataset.id) : [];
            if (desc.length) anchor = desc[desc.length - 1];
            anchor.insertAdjacentElement('afterend', temp);
        } else {
            const table = document.getElementById('calpTable');
            if (!table) return;
            table.appendChild(temp);
        }
        input.focus();
        let done = false;
        function close() {
            if (done) return;
            done = true;
            temp.remove();
            if (after && after.dataset.id) focusRow(rowById(after.dataset.id));
        }
        function save(next) {
            if (done) return;
            const title = input.value.trim();
            if (!title) { close(); return; }
            done = true;
            const body = createBody(title, start, end, parent, order, prefill);
            const detected = qt.time();
            qt.applyTo(body); // 감지된 날짜·시각·반복 (AZ2)
            chipEl.remove();
            // 낙관적: 입력 줄을 바로 행처럼 보이게 두고, id 가 오면 진짜 행이 된다.
            const span = document.createElement('span');
            span.className = 'calp-td-text';
            span.textContent = body.get('title') || title;
            input.replaceWith(span);
            temp.classList.remove('calp-temp');
            temp.classList.add('pending');
            temp.dataset.title = body.get('title') || title;
            temp.dataset.parent = parent || '';
            temp.dataset.order = String(order);
            temp.dataset.depth = String(depth);
            temp.dataset.start = body.get('start_date') || '';
            temp.dataset.end = body.get('end_date') || '';
            temp.dataset.done = '0';
            temp.dataset.isowner = '1';
            temp.dataset.color = calColor;
            if (next) createSiblingAfter(null, false, temp); // 다음 줄은 바로 연다
            api.create(body)
                .then((ev) => applyPrefill(ev, prefill))
                .then((ev) => {
                    temp.dataset.id = ev.id;
                    temp.classList.remove('pending');
                    if (!focusedId || focusedId === 'pending') focusedId = ev.id;
                    if (ev.series) { // 반복: 시리즈 생성은 실행 취소 밖 — 행들이 한꺼번에 생기므로 곧바로 다시 그린다
                        showToast(fmtCount(H.msgSeriesCreated || '반복 일정 %d개를 만들었습니다', ev.created_count || 1));
                        return refreshGrid().catch(() => {});
                    }
                    return recordQuiet(createCommand(ev.id), { delay: detected && detected.date ? 0 : 600 });
                })
                .catch(() => { temp.remove(); fail(); });
        }
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); save(!(e.shiftKey || e.ctrlKey || e.metaKey)); }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
            else if (e.key === 'Tab') { e.preventDefault(); if (!e.shiftKey && after && !asChild) { const t = input.value; temp.remove(); done = true; const again = createSiblingAfter(after.dataset.id, true); if (again) again.value = t; } }
        });
        input.addEventListener('blur', () => setTimeout(() => save(false), 100));
        return input;
    }
    function maxOrderOf(list) {
        return list.reduce((m, r) => Math.max(m, num(r)), 0);
    }

    // ── 계층/순서/날짜 조작 ──
    function indent(list) {
        const cmds = [];
        list.forEach((row) => {
            const prev = prevSibling(row);
            if (!prev || list.includes(prev)) return;
            const kids = children(prev.dataset.id);
            cmds.push(patchCommand(row.dataset.id,
                { parent_id: row.dataset.parent || '', sort_order: String(num(row)) },
                { parent_id: prev.dataset.id, sort_order: String(kids.length ? maxOrderOf(kids) + 1 : 1) }));
            folded.delete(prev.dataset.id);
        });
        if (!cmds.length) return Promise.resolve();
        saveFolded();
        return commitQuiet(composite(cmds), { refresh: 'now' });
    }
    function outdent(list) {
        const cmds = [];
        list.forEach((row) => {
            const parent = row.dataset.parent ? rowById(row.dataset.parent) : null;
            if (!parent || list.includes(parent)) return;
            cmds.push(patchCommand(row.dataset.id,
                { parent_id: row.dataset.parent, sort_order: String(num(row)) },
                { parent_id: parent.dataset.parent || '', sort_order: String(orderAfter(parent)) }));
        });
        if (!cmds.length) return Promise.resolve();
        return commitQuiet(composite(cmds), { refresh: 'now' });
    }
    function move(row, dir) {
        const neighbor = dir < 0 ? prevSibling(row) : nextSibling(row);
        if (!neighbor) return;
        if (sortModeNow() === 'date' && neighbor.dataset.start !== row.dataset.start) {
            // 시작일 정렬: 같은 날짜 안에서만 순서를 바꾼다 (날짜는 Alt+←→ 또는 드래그로)
            return;
        }
        const order = dir < 0 ? orderBefore(neighbor) : orderAfter(neighbor);
        // 낙관적: DOM 에서 먼저 옮긴다 (자손 포함)
        const block = [row].concat(descendants(row.dataset.id));
        const nb = [neighbor].concat(descendants(neighbor.dataset.id));
        if (dir < 0) block.forEach((el) => neighbor.parentNode.insertBefore(el, neighbor));
        else { const last = nb[nb.length - 1]; block.reverse().forEach((el) => last.insertAdjacentElement('afterend', el)); }
        row.dataset.order = String(order);
        focusRow(row);
        commitQuiet(patchCommand(row.dataset.id, { sort_order: String(num(row)) }, { sort_order: String(order) }));
    }
    // 날짜 낙관적 반영: 텍스트 + 미니 바 위치
    const TBD = () => (document.getElementById('calendarModal') || { dataset: {} }).dataset.lTbd || '미정';
    function applyDatesLocal(r, start, end) {
        r.dataset.start = start || ''; r.dataset.end = end || '';
        const sb = r.querySelector('.calp-when-btn[data-which="start"]');
        const eb = r.querySelector('.calp-when-btn[data-which="end"]');
        if (sb) { sb.textContent = start || TBD(); sb.classList.toggle('tbd', !start); }
        if (eb) { eb.textContent = end || TBD(); eb.classList.toggle('tbd', !end); }
        let bar = r.querySelector('.calp-bar[data-eid]');
        const track = r.querySelector('.calp-track');
        const days = Number(hub.dataset.spanDays) || 0;
        if (!start && !end) { if (bar) bar.remove(); return; }
        const s = start || end, en = end || start;
        if (track && days && hub.dataset.spanStart) {
            if (!bar) { bar = document.createElement('span'); bar.className = 'calp-bar cal-' + (r.dataset.color || 'c1') + ' active'; bar.dataset.eid = r.dataset.id; track.appendChild(bar); }
            bar.dataset.start = s; bar.dataset.end = en;
            const d0 = Math.round((new Date(s + 'T00:00:00') - new Date(hub.dataset.spanStart + 'T00:00:00')) / 86400000);
            const len = Math.round((new Date(en + 'T00:00:00') - new Date(s + 'T00:00:00')) / 86400000) + 1;
            bar.style.left = (Math.max(0, d0) / days * 100) + '%';
            bar.style.width = (Math.max(1, len) / days * 100) + '%';
        }
    }
    // 반복 인스턴스가 섞이면 한 번 묻는다 (BA1, calendar-scope.js): 「이 일정만」은 평소 길(낙관적 + 실행 취소),
    // 「이후 모두」·「전체」는 보통 행은 평소대로 · 반복은 시리즈마다 한 번 series_scope 로 (실행 취소 밖).
    function shiftDates(list, days) {
        if (!list.length) return;
        const dated = list.filter((r) => r.dataset.start || r.dataset.end);
        if (!dated.length) return;
        const next = (r) => ({ start_date: r.dataset.start ? addDays(r.dataset.start, days) : '', end_date: r.dataset.end ? addDays(r.dataset.end, days) : '' });
        splitScope(dated, 'edit').then((sp) => {
            if (!sp) return;
            const cmds = sp.plain.map((r) => {
                const n = next(r);
                const cmd = patchCommand(r.dataset.id, { start_date: r.dataset.start, end_date: r.dataset.end }, n);
                applyDatesLocal(r, n.start_date, n.end_date);
                return cmd;
            });
            if (cmds.length) commitQuiet(composite(cmds));
            if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, next);
        });
    }
    function setDates(list, start, end) {
        splitScope(list, 'edit').then((sp) => {
            if (!sp) return;
            const cmds = sp.plain.map((r) => {
                const cmd = patchCommand(r.dataset.id, { start_date: r.dataset.start, end_date: r.dataset.end }, { start_date: start, end_date: end });
                applyDatesLocal(r, start, end);
                return cmd;
            });
            if (cmds.length) commitQuiet(composite(cmds));
            if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, () => ({ start_date: start, end_date: end }));
        });
    }
    function toggleDone(list) {
        if (!list.length) return;
        const allDone = list.every((r) => r.dataset.done === '1');
        const cmds = list.map((r) => {
            const cmd = patchCommand(r.dataset.id, { done: r.dataset.done === '1' ? '1' : '0' }, { done: allDone ? '0' : '1' });
            r.dataset.done = allDone ? '0' : '1';
            r.classList.toggle('is-done', !allDone);
            const ring = r.querySelector('.calp-done');
            if (ring) ring.checked = !allDone;
            const st = r.querySelector('.calp-status');
            if (st) {
                if (!allDone) { st.dataset.prevText = st.textContent; st.dataset.prevClass = st.className; st.textContent = (document.getElementById('calendarModal') || { dataset: {} }).dataset.lDone || '완료'; st.className = 'calp-status done'; }
                else if (st.dataset.prevText) { st.textContent = st.dataset.prevText; st.className = st.dataset.prevClass || 'calp-status active'; }
            }
            return cmd;
        });
        commitQuiet(composite(cmds));
    }
    // 숨기기: 행이 목록에서 빠지므로(「숨긴 작업 보기」가 꺼져 있으면) 조용한 갱신이 아니라
    // 토스트(실행 취소)와 함께 다시 그린다 — calendar-list.js 의 toggleHidden 과 같은 규칙.
    function toggleHidden(list) {
        if (!list.length) return;
        const allHidden = list.every((r) => r.dataset.hidden === '1');
        const cmds = list.map((r) => patchCommand(r.dataset.id, { hidden: r.dataset.hidden === '1' ? '1' : '0' }, { hidden: allHidden ? '0' : '1' }));
        const L = labels();
        clearSelection();
        commit(composite(cmds), list.length === 1 ? (allHidden ? L.unhidden : L.hidden) : fmtCount(L.bulkHidden, list.length));
    }
    function setColor(list, color) {
        splitScope(list, 'edit').then((sp) => {
            if (!sp) return;
            const cmds = sp.plain.map((r) => {
                const cmd = patchCommand(r.dataset.id, { color: r.dataset.color || 'c1' }, { color });
                r.dataset.color = color;
                r.querySelectorAll('.calp-swatch, .calp-done, .calp-bar[data-eid]').forEach((el) => { el.className = el.className.replace(/\bcal-c\d\b/, 'cal-' + color); });
                return cmd;
            });
            if (cmds.length) commitQuiet(composite(cmds));
            if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, () => ({ color }));
        });
    }
    function remove(list) {
        const mine = list.filter((r) => r.dataset.isowner === '1');
        if (!mine.length) { showToast(labels().ownerOnly, { error: true }); return; }
        const L = labels();
        splitScope(mine, 'delete').then((sp) => {
            if (!sp) return;
            clearSelection();
            if (sp.plain.length) {
                commit(composite(sp.plain.map((r) => deleteCommand(r.dataset.id))), sp.plain.length === 1 ? L.deleted : fmtCount(L.bulkDeleted, sp.plain.length))
                    .then(() => { if (mine.length < list.length) showToast(L.ownerOnly); });
            }
            if (sp.scoped.length) deleteScoped(sp.scoped, sp.scope);
        });
    }
    function duplicate(list) {
        if (!list.length) return;
        const L = labels();
        let order = maxOrder() + 1;
        const seq = list.reduce((p, r) => p.then((acc) => {
            const b = createBody(r.dataset.title, r.dataset.start, r.dataset.end, r.dataset.parent || '', order);
            order += 1;
            b.set('color', r.dataset.color || calColor);
            b.set('memo', r.dataset.memo || '');
            return api.create(b).then((ev) => acc.concat([ev]));
        }), Promise.resolve([]));
        seq.then((created) => {
            clearSelection();
            void L;
            return recordQuiet(composite(created.map((ev) => createCommand(ev.id))), { refresh: 'now' });
        }).catch(fail);
    }

    // ── 여러 줄 일괄 생성 ──
    function createMany(lines) {
        if (!lines.length) return Promise.resolve();
        const base = maxOrder() + 1;
        const stack = [];
        const seq = lines.reduce((p, line, i) => p.then((acc) => {
            const parent = line.depth > 0 ? (stack[line.depth - 1] || '') : '';
            return api.create(createBody(line.title, '', '', parent, base + i)).then((ev) => {
                stack[line.depth] = ev.id;
                stack.length = line.depth + 1;
                return acc.concat([ev]);
            });
        }), Promise.resolve([]));
        return seq.then((created) => recordQuiet(composite(created.map((ev) => createCommand(ev.id))), { refresh: 'now' })).catch(fail);
    }
    const quickInput = () => document.getElementById('calpQuickInput');
    hub.addEventListener('paste', (e) => {
        if (e.target !== quickInput()) return;
        const text = (e.clipboardData || window.clipboardData).getData('text');
        if (!text || !/\n/.test(text.trim())) return;
        e.preventDefault();
        e.target.value = '';
        createMany(parseOutline(text));
    });
    // 일괄 입력창: #projHub 안에 있어 소프트 갱신 때 교체되므로 전부 위임한다.
    const bulkEl = () => document.getElementById('calpBulk');
    const bulkTextEl = () => document.getElementById('calpBulkText');
    function submitBulk() {
        const bulk = bulkEl(), text = bulkTextEl();
        if (!bulk || !text) return;
        const lines = parseOutline(text.value);
        if (!lines.length) return;
        bulk.hidden = true;
        text.value = '';
        createMany(lines);
    }
    hub.addEventListener('click', (e) => {
        const bulk = bulkEl();
        if (!bulk) return;
        if (e.target.closest('#calpBulkToggle')) {
            bulk.hidden = !bulk.hidden;
            if (!bulk.hidden) bulkTextEl().focus();
        } else if (e.target.closest('#calpBulkCancel')) {
            bulk.hidden = true;
        } else if (e.target.closest('#calpBulkAdd')) {
            submitBulk();
        }
    });
    hub.addEventListener('keydown', (e) => {
        if (e.target.id !== 'calpBulkText') return;
        const text = e.target;
        if (e.key === 'Tab') { // 텍스트 영역에서 Tab 은 들여쓰기 입력
            e.preventDefault();
            const s = text.selectionStart, en = text.selectionEnd;
            text.value = text.value.slice(0, s) + '  ' + text.value.slice(en);
            text.selectionStart = text.selectionEnd = s + 2;
        } else if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            submitBulk();
        } else if (e.key === 'Escape') {
            bulkEl().hidden = true;
        }
    });

    // ── 일괄 기간 팝오버 ──
    const datesPop = document.getElementById('calpDatesPop');
    function hideDatesPop() { if (datesPop) datesPop.hidden = true; }
    if (datesPop) {
        const sEl = document.getElementById('calpBulkStart'), eEl = document.getElementById('calpBulkEnd');
        const sBtn = document.getElementById('calpBulkStartBtn'), eBtn = document.getElementById('calpBulkEndBtn');
        const ph = datesPop.dataset.pick || '날짜 선택';
        const base = { weekdays: datesPop.dataset.weekdays, placeholder: ph };
        attachDatePicker(sBtn, sEl, Object.assign({}, base, { onSet: (v) => { if (!eEl.value || eEl.value < v) eEl.value = v; syncDateLabel(sBtn, sEl, ph); syncDateLabel(eBtn, eEl, ph); } }));
        attachDatePicker(eBtn, eEl, Object.assign({}, base, { onSet: (v) => { if (sEl.value && v < sEl.value) sEl.value = v; syncDateLabel(sBtn, sEl, ph); syncDateLabel(eBtn, eEl, ph); } }));
        document.getElementById('calpBulkDatesApply').addEventListener('click', () => {
            if (!sEl.value || !eEl.value) return;
            const list = targets();
            hideDatesPop();
            setDates(list, sEl.value, eEl.value);
        });
        ['mousedown', 'click'].forEach((t) => datesPop.addEventListener(t, (e) => e.stopPropagation()));
    }
    function showDatesPop() {
        if (!datesPop) return;
        const first = targets()[0];
        const sEl = document.getElementById('calpBulkStart'), eEl = document.getElementById('calpBulkEnd');
        sEl.value = first ? first.dataset.start : today;
        eEl.value = first ? first.dataset.end : today;
        const ph = datesPop.dataset.pick || '날짜 선택';
        syncDateLabel(document.getElementById('calpBulkStartBtn'), sEl, ph);
        syncDateLabel(document.getElementById('calpBulkEndBtn'), eEl, ph);
        datesPop.hidden = false;
    }

    // ── 정렬 모드 (서버 렌더): date = 시작일 → 같은 날은 수동, manual = 수동, 그 외 열 정렬 ──
    // 정렬 모드는 SPA 이동으로 바뀌므로 루트의 data-sort-mode 를 그때그때 읽는다
    const sortModeNow = () => hub.dataset.sortMode || 'date';
    const sortStateNow = () => { const m = sortModeNow(); return m !== 'date' && m !== 'manual' ? { key: m } : null; }; // 열 정렬 중엔 드래그 금지
    // 정렬 컨트롤은 뷰 툴바(calendar-views.js 의 정렬 ▾ · 헤더 클릭)로 옮겨 갔다.
    rows().forEach((r) => r.classList.toggle('sorted', !!sortStateNow()));
    document.addEventListener('calendar:refreshed', () => rows().forEach((r) => r.classList.toggle('sorted', !!sortStateNow())));

    // ── 시작일/종료일 각각 지정 (기간 텍스트의 날짜를 클릭) ──
    hub.addEventListener('click', (e) => {
        const btn = e.target.closest('.calp-when-btn');
        if (!btn) return;
        const row = btn.closest('.calp-tr[data-id]');
        // 이미 피커가 붙어 있으면(내부 트리거의 합성 클릭) 그대로 흘려보낸다 — 여기서 막으면 피커가 안 열린다.
        if (!row || btn.querySelector('.att-dp')) return;
        e.stopPropagation();
        e.preventDefault();
        const which = btn.dataset.which;
        const wrap = document.createElement('span');
        wrap.className = 'att-dp';
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.value = which === 'start' ? row.dataset.start : row.dataset.end;
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'att-dp-btn'; // 데이트피커의 바깥 클릭 판정에서 제외되도록
        trigger.style.cssText = 'position:absolute; width:0; height:0; padding:0; border:0; opacity:0;';
        wrap.appendChild(hidden);
        wrap.appendChild(trigger);
        btn.appendChild(wrap);
        const pop = document.getElementById('calpDatesPop');
        attachDatePicker(trigger, hidden, {
            weekdays: pop ? pop.dataset.weekdays : '', placeholder: '', clearLabel: TBD(),
            onSet: (v) => {
                wrap.remove();
                let start = row.dataset.start, end = row.dataset.end;
                if (which === 'start') { start = v; if (end && start && end < start) end = start; } else { end = v; if (start && end && start > end) start = end; }
                if (start === row.dataset.start && end === row.dataset.end) return;
                splitScope([row], 'edit').then((sp) => { // 반복 인스턴스는 범위를 묻는다 (BA1)
                    if (!sp) return;
                    if (sp.scoped.length) { sendScoped(sp.scoped, sp.scope, () => ({ start_date: start, end_date: end })); return; }
                    const cmd = patchCommand(row.dataset.id, { start_date: row.dataset.start, end_date: row.dataset.end }, { start_date: start, end_date: end });
                    applyDatesLocal(row, start, end);
                    commitQuiet(cmd);
                });
            },
        });
        trigger.click();
        // 팝오버 밖 클릭이나 Esc 로 닫히면 임시 요소 정리
        const cleanup = () => { setTimeout(() => { if (!document.querySelector('.att-dp-pop')) wrap.remove(); }, 0); document.removeEventListener('mousedown', cleanup, true); };
        setTimeout(() => document.addEventListener('mousedown', cleanup, true), 0);
    }, true);

    // ── 행 드래그: 순서·계층 변경 (⋮⋮ 손잡이). 가로 위치가 깊이를 정한다 ──
    let rdrag = null;
    hub.addEventListener('mousedown', (e) => {
        if (e.button !== 0) return;
        const grip = e.target.closest('.calp-grip');
        if (!grip) return;
        const row = grip.closest('.calp-tr[data-id]');
        if (!row || sortStateNow()) return;
        e.preventDefault();
        const roots = selected.has(row.dataset.id)
            ? rows().filter((r) => selected.has(r.dataset.id) && !(r.dataset.parent && selected.has(r.dataset.parent)))
            : [row];
        const moving = new Set();
        roots.forEach((r) => { moving.add(r.dataset.id); descendants(r.dataset.id).forEach((d) => moving.add(d.dataset.id)); });
        const ghost = document.createElement('div');
        ghost.className = 'calp-drag-ghost';
        ghost.textContent = roots.length === 1 ? row.dataset.title : fmtCount(labels().selected, roots.length);
        document.body.appendChild(ghost);
        const line = document.createElement('div');
        line.className = 'calp-drop-line';
        document.getElementById('calpTable').appendChild(line);
        rdrag = { roots, moving, ghost, line, target: null, moved: false, x0: e.clientX, y0: e.clientY };
        beginDrag();
        rows().forEach((r) => { if (moving.has(r.dataset.id)) r.classList.add('drag-src'); });
    });
    document.addEventListener('mousemove', (e) => {
        if (!rdrag) return;
        if (!rdrag.moved && Math.abs(e.clientX - rdrag.x0) < 3 && Math.abs(e.clientY - rdrag.y0) < 3) return;
        rdrag.moved = true;
        rdrag.ghost.style.left = (e.clientX + 12) + 'px';
        rdrag.ghost.style.top = (e.clientY + 8) + 'px';
        const list = visibleRows().filter((r) => !rdrag.moving.has(r.dataset.id));
        let index = list.length;
        for (let i = 0; i < list.length; i += 1) {
            const rc = list[i].getBoundingClientRect();
            if (e.clientY < rc.top + rc.height / 2) { index = i; break; }
        }
        const prev = list[index - 1] || null;
        const next = list[index] || null;
        const maxDepth = prev ? parseInt(prev.dataset.depth || '0', 10) + 1 : 0;
        const minDepth = next ? parseInt(next.dataset.depth || '0', 10) : 0;
        const table = document.getElementById('calpTable');
        const tr = table.getBoundingClientRect();
        const titleLeft = tr.left + 52 + 24; // check col + padding
        let depth = Math.round((e.clientX - titleLeft) / 18);
        depth = Math.max(Math.min(minDepth, maxDepth), Math.min(maxDepth, depth));
        rdrag.target = { index, prev, next, depth };
        const y = next ? next.getBoundingClientRect().top : (prev ? prev.getBoundingClientRect().bottom : tr.top + 34);
        rdrag.line.style.top = (y - tr.top - 1) + 'px';
        rdrag.line.style.left = (52 + 12 + depth * 18) + 'px';
        rdrag.line.style.right = '12px';
    });
    document.addEventListener('mouseup', () => {
        if (!rdrag) return;
        const d = rdrag;
        rdrag = null;
        endDrag();
        d.ghost.remove();
        d.line.remove();
        rows().forEach((r) => r.classList.remove('drag-src'));
        if (!d.moved || !d.target) return;
        const { prev, depth } = d.target;
        // 부모: 삽입 위치 앞쪽에서 depth-1 인 가장 가까운 행
        let parent = '';
        if (depth > 0) {
            const list = visibleRows().filter((r) => !d.moving.has(r.dataset.id));
            const upto = prev ? list.indexOf(prev) : -1;
            for (let i = upto; i >= 0; i -= 1) {
                if (parseInt(list[i].dataset.depth || '0', 10) === depth - 1) { parent = list[i].dataset.id; break; }
            }
        }
        const sib = siblings(parent).filter((r) => !d.moving.has(r.dataset.id));
        // prev 쪽 형제: prev 자체가 형제거나 prev 의 조상 중 형제
        let prevSib = null;
        if (prev) {
            let cur = prev;
            while (cur && (cur.dataset.parent || '') !== parent) cur = cur.dataset.parent ? rowById(cur.dataset.parent) : null;
            if (cur && (cur.dataset.parent || '') === parent) prevSib = cur;
        }
        const pi = prevSib ? sib.indexOf(prevSib) : -1;
        const nextSib = sib[pi + 1] || null;
        const lo = prevSib ? num(prevSib) : (nextSib ? num(nextSib) - d.roots.length - 1 : 0);
        const hi = nextSib ? num(nextSib) : lo + d.roots.length + 1;
        const step = (hi - lo) / (d.roots.length + 1);
        // 시작일 정렬 모드: 다른 날짜 그룹 사이에 놓으면 그 날짜로 옮긴다 (기간 길이 유지).
        const anchorRow = prevSib || nextSib;
        // 미정 그룹(맨 아래)으로 옮기면 날짜를 비우고, 미정 작업을 날짜 그룹에 놓으면 그 날 하루로 잡는다.
        const groupDate = sortModeNow() === 'date' && anchorRow ? (anchorRow.dataset.start || '') : null;
        const cmds = d.roots.map((r, i) => {
            const before = { parent_id: r.dataset.parent || '', sort_order: String(num(r)) };
            const after = { parent_id: parent, sort_order: String(lo + step * (i + 1)) };
            if (groupDate !== null && groupDate !== (r.dataset.start || '')) {
                before.start_date = r.dataset.start || ''; before.end_date = r.dataset.end || '';
                if (!groupDate) { after.start_date = ''; after.end_date = ''; }
                else if (!r.dataset.start) { after.start_date = groupDate; after.end_date = groupDate; }
                else {
                    const len = r.dataset.end ? Math.round((new Date(r.dataset.end + 'T00:00:00') - new Date(r.dataset.start + 'T00:00:00')) / 86400000) : 0;
                    after.start_date = groupDate; after.end_date = addDays(groupDate, len);
                }
            }
            return patchCommand(r.dataset.id, before, after);
        });
        if (parent) { folded.delete(parent); saveFolded(); }
        commitQuiet(composite(cmds), { refresh: 'now' });
    });
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape' || !rdrag) return;
        rdrag.ghost.remove();
        rdrag.line.remove();
        rows().forEach((r) => r.classList.remove('drag-src'));
        rdrag = null;
        endDrag();
        e.stopImmediatePropagation();
    }, true);

    // ── 선행 작업 (finish-to-start): 칩 클릭·우클릭 메뉴로 자유롭게 지정 ──
    function csvDeps(v) { return (v || '').split(',').filter(Boolean); }
    function paintDepChip(row) {
        const cell = row.querySelector('.calp-td-title, .calp-row-title');
        if (!cell) return;
        let chip = row.querySelector('.calp-deps');
        const ids = csvDeps(row.dataset.deps);
        if (!ids.length) { if (chip) chip.remove(); return; }
        if (!chip) { chip = document.createElement('button'); chip.type = 'button'; chip.className = 'calp-deps'; cell.appendChild(chip); }
        chip.textContent = '⇠ ' + ids.length;
        chip.title = (hub.dataset.depsTitle || '선행 작업') + ': ' + ids.map((id) => { const r = rowById(id); return r ? r.dataset.title : id; }).join(', ');
    }
    function applyDeps(row, ids) {
        const before = row.dataset.deps || '';
        const after = ids.join(',');
        if (before === after) return;
        row.dataset.deps = after;
        paintDepChip(row);
        commitQuiet(patchCommand(row.dataset.id, { dep_ids: before }, { dep_ids: after }));
    }
    let depsPop = null;
    function closeDepsPop() { if (depsPop) { depsPop.remove(); depsPop = null; } }
    function openDepsPicker(row, anchor) {
        closeDepsPop();
        const cur = new Set(csvDeps(row.dataset.deps));
        const others = rows().filter((r) => r !== row);
        const pop = document.createElement('div');
        pop.className = 'calp-deps-pop';
        pop.innerHTML = '<div class="calp-deps-head"></div><input type="text" class="calp-deps-search"><div class="calp-deps-list"></div>';
        pop.querySelector('.calp-deps-head').textContent = (hub.dataset.depsTitle || '선행 작업') + ' — ' + row.dataset.title;
        const search = pop.querySelector('.calp-deps-search');
        search.placeholder = hub.dataset.depsSearch || '작업 검색…';
        const list = pop.querySelector('.calp-deps-list');
        function renderList() {
            const q = search.value.trim().toLowerCase();
            list.innerHTML = '';
            others.filter((r) => !q || (r.dataset.title || '').toLowerCase().includes(q)).forEach((r) => {
                const l = document.createElement('label');
                l.className = 'calp-deps-item';
                const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = cur.has(r.dataset.id);
                const t = document.createElement('span'); t.textContent = r.dataset.title;
                l.appendChild(cb); l.appendChild(t);
                cb.addEventListener('change', () => { if (cb.checked) cur.add(r.dataset.id); else cur.delete(r.dataset.id); applyDeps(row, Array.from(cur)); });
                list.appendChild(l);
            });
            if (!list.children.length) { const e = document.createElement('div'); e.className = 'calp-deps-item'; e.textContent = hub.dataset.depsNone || '선행 작업 없음'; list.appendChild(e); }
        }
        search.addEventListener('input', renderList);
        renderList();
        document.body.appendChild(pop);
        const r = (anchor || row).getBoundingClientRect();
        pop.style.left = Math.max(4, Math.min(r.left, window.innerWidth - 296)) + 'px';
        pop.style.top = Math.max(4, Math.min(r.bottom + 4, window.innerHeight - Math.min(320, pop.offsetHeight) - 8)) + 'px';
        depsPop = pop;
        search.focus();
        const away = (e) => { if (pop.contains(e.target)) return; closeDepsPop(); document.removeEventListener('mousedown', away, true); };
        setTimeout(() => document.addEventListener('mousedown', away, true), 0);
        pop.addEventListener('keydown', (e) => { if (e.key === 'Escape') { e.stopPropagation(); closeDepsPop(); } });
    }
    hub.addEventListener('click', (e) => {
        const chip = e.target.closest('.calp-deps');
        if (!chip) return;
        e.preventDefault(); e.stopPropagation();
        const row = chip.closest('.calp-tr[data-id], .calp-row[data-id]');
        if (row) openDepsPicker(row, chip);
    }, true);

    // ── 우클릭 메뉴 ──
    hub.addEventListener('contextmenu', (e) => {
        if (e.target.closest('input, textarea, .calp-edit')) return;
        const M = hub.dataset;
        const row = e.target.closest('.calp-tr[data-id]');
        e.preventDefault();
        if (!row) {
            showContextMenu(e.clientX, e.clientY, [
                { label: M.menuNew || '새 작업', onClick: () => createSiblingAfter(rows().length ? rows()[rows().length - 1].dataset.id : null, false) },
                { label: M.menuBulk || '여러 작업 붙여넣기', onClick: () => { const b = bulkEl(); if (b) { b.hidden = false; bulkTextEl().focus(); } } },
            ]);
            return;
        }
        if (!selected.has(row.dataset.id)) { clearSelection(); }
        focusedId = row.dataset.id;
        const list = targets().length ? targets() : [row];
        const many = list.length > 1;
        showContextMenu(e.clientX, e.clientY, [
            { label: M.menuOpen || '열기', hint: 'Ctrl+Enter', disabled: many, onClick: () => window.openCalendarEvent(row) },
            { label: M.menuRename || '제목 편집', hint: 'Enter', disabled: many, onClick: () => startEdit(row) },
            { label: M.menuSibling || '아래에 작업 추가', disabled: many, onClick: () => createSiblingAfter(row.dataset.id, false) },
            { label: M.menuChild || '하위 작업 추가', disabled: many, onClick: () => createSiblingAfter(row.dataset.id, true) },
            { separator: true },
            { label: M.menuDone || '완료/미완료', hint: 'Space', onClick: () => toggleDone(list) },
            // 숨기기 (AM2): 월 뷰·표·패널과 같은 patch hidden — 모두 숨겨져 있으면 되돌린다
            { label: list.every((r) => r.dataset.hidden === '1') ? (M.menuUnhide || '숨기기 해제') : (M.menuHide || '숨기기'), onClick: () => toggleHidden(list) },
            { colors: ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'], onColor: (c) => setColor(list, c) },
            { label: M.menuDates || '기간…', onClick: () => { list.forEach((r) => selected.add(r.dataset.id)); applySelection(); showDatesPop(); } },
            { separator: true },
            { label: M.menuDeps || '선행 작업…', disabled: many, onClick: () => openDepsPicker(row, row.querySelector('.calp-deps') || row.querySelector('.calp-td-title, .calp-row-title') || row) },
            { label: M.menuDepsPrev || '바로 위 작업을 선행으로', disabled: many || !prevSibling(row), onClick: () => { const pv = prevSibling(row); if (pv) applyDeps(row, Array.from(new Set(csvDeps(row.dataset.deps).concat(pv.dataset.id)))); } },
            { separator: true },
            { label: M.menuIndent || '들여쓰기', hint: 'Tab', onClick: () => indent(list) },
            { label: M.menuOutdent || '내어쓰기', hint: 'Shift+Tab', onClick: () => outdent(list) },
            { label: M.menuUp || '위로', hint: 'Alt+↑', disabled: many || !!sortStateNow(), onClick: () => move(row, -1) },
            { label: M.menuDown || '아래로', hint: 'Alt+↓', disabled: many || !!sortStateNow(), onClick: () => move(row, 1) },
            { separator: true },
            { label: M.menuDup || '복제', hint: 'Ctrl+D', onClick: () => duplicate(list) },
            { label: M.menuDelete || '삭제', hint: 'Del', danger: true, disabled: !list.some((r) => r.dataset.isowner === '1'), onClick: () => remove(list) },
        ]);
    });

    // ── 프로젝트 속성(열): 인라인 셀 편집 ──
    let schema = [];
    try { schema = (JSON.parse(hub.dataset.schema || '{}') || {})[calId] || []; } catch (_) { schema = []; }
    const modalEl = document.getElementById('calendarModal');
    const L2 = () => (modalEl ? modalEl.dataset : {});
    const usersList = () => { const sel = document.getElementById('calAllUsers') || document.getElementById('calShareUsers'); return sel ? Array.from(sel.options).map((o) => ({ label: o.textContent.trim(), value: o.value })) : []; };
    const defOf = (id) => schema.find((p) => p.id === id);
    function parseMulti(v) {
        if (!v) return [];
        try { const a = JSON.parse(v); return Array.isArray(a) ? a : [String(v)]; } catch (_) { return [v]; }
    }
    // 셀을 새 값으로 즉시 다시 그린다 (낙관적)
    function paintCell(cell, def, value) {
        cell.dataset.value = value;
        // 관계·롤업·자동 번호는 서버가 그린 마크업을 지킨다 — 관계는 patch 뒤 refresh 로 다시 온다
        if (def.type === 'relation' || def.type === 'rollup' || def.type === 'autonumber' || def.type === 'formula') return;
        cell.innerHTML = '';
        if (def.type === 'users') {
            const ids = parseMulti(value);
            if (!ids.length) { const e = document.createElement('span'); e.className = 'calp-prop-empty'; e.textContent = '—'; cell.appendChild(e); return; }
            ids.slice(0, 3).forEach((id) => { const u = usersList().find((x) => x.value === id); cell.appendChild(pill(u ? u.label : id, '', true)); });
            if (ids.length > 3) cell.appendChild(pill('+' + (ids.length - 3), '', true));
            return;
        }
        if (def.type === 'files') {
            let refs = []; try { refs = JSON.parse(value || '[]') || []; } catch (_) { refs = []; }
            const t = document.createElement('span'); t.className = refs.length ? 'calp-prop-text' : 'calp-prop-empty'; t.textContent = refs.length ? '📎 ' + refs.length : '—'; cell.appendChild(t);
            return;
        }
        if (def.type === 'status') {
            if (!value) { const e = document.createElement('span'); e.className = 'calp-prop-empty'; e.textContent = '—'; cell.appendChild(e); return; }
            const o = (def.options || []).find((x) => x.label === value);
            const p = pill(value, o ? o.color : 'o8'); p.dataset.group = (o && o.group) || 'todo'; cell.appendChild(p);
            return;
        }
        if (def.type === 'check') {
            const c = document.createElement('input');
            c.type = 'checkbox';
            c.className = 'calp-prop-check';
            c.checked = value === '1';
            cell.appendChild(c);
            return;
        }
        if (def.type === 'select' || def.type === 'multiselect') {
            const vals = def.type === 'multiselect' ? parseMulti(value) : (value ? [value] : []);
            if (!vals.length) { const e = document.createElement('span'); e.className = 'calp-prop-empty'; e.textContent = '—'; cell.appendChild(e); return; }
            vals.forEach((v) => { const o = (def.options || []).find((x) => x.label === v); cell.appendChild(pill(v, o ? o.color : 'o8')); });
            return;
        }
        let display = value;
        if (def.type === 'user') { const u = usersList().find((x) => x.value === value); display = u ? u.label : value; }
        if (!display) { const e = document.createElement('span'); e.className = 'calp-prop-empty'; e.textContent = '—'; cell.appendChild(e); return; }
        if (def.type === 'url') { const a = document.createElement('a'); a.className = 'calp-prop-link'; a.href = value; a.target = '_blank'; a.rel = 'noopener'; a.textContent = value; cell.appendChild(a); return; }
        const t = document.createElement('span');
        t.className = 'calp-prop-text';
        t.textContent = display;
        cell.appendChild(t);
    }
    // 관계 칩을 낙관적으로 (서버의 cal-prop-cell "chips" 와 같은 마크업; 🔒 비공개 수는 서버가 다시 그릴 때까지 둔다)
    function paintRelationChips(cell, list) {
        const hidden = cell.querySelector('.cal-prop-hidden');
        const box = document.createElement('span'); box.className = 'cal-prop-chips';
        list.forEach((it) => {
            const a = document.createElement('a'); a.className = 'cal-rel-chip cal-' + (it.color || 'c1');
            a.href = '/calendar/events/' + encodeURIComponent(it.id); a.dataset.relId = it.id; a.setAttribute('data-no-spa', ''); a.textContent = it.title;
            box.appendChild(a);
        });
        if (hidden) box.appendChild(hidden);
        if (!list.length && !hidden) { const e = document.createElement('span'); e.className = 'calp-prop-empty'; e.textContent = '—'; box.appendChild(e); }
        cell.innerHTML = '';
        cell.appendChild(box);
    }
    function setCellValue(row, cell, def, value) {
        const before = cell.dataset.value || '';
        if (before === value) return;
        const fields = {};
        fields['prop:' + def.id] = value;
        const prev = {};
        prev['prop:' + def.id] = before;
        // 반복 인스턴스의 속성 값은 범위를 묻는다 (BA1) — 취소하면 칸은 그대로
        splitScope([row], 'edit').then((sp) => {
            if (!sp) return;
            if (sp.scoped.length) { sendScoped(sp.scoped, sp.scope, () => fields); return; }
            paintCell(cell, def, value);
            commitQuiet(patchCommand(row.dataset.id, prev, fields));
        });
    }
    function editCell(row, cell) {
        const def = defOf(cell.dataset.prop);
        if (!def) return;
        const value = cell.dataset.value || '';
        if (def.type === 'check') {
            setCellValue(row, cell, def, value === '1' ? '' : '1');
            return;
        }
        if (def.type === 'select' || def.type === 'multiselect') {
            const multi = def.type === 'multiselect';
            openPillPicker(cell, {
                options: def.options || [], multi, allowNew: true,
                values: multi ? parseMulti(value) : (value ? [value] : []),
                noneLabel: L2().lNone, newLabel: L2().lNewOption, placeholder: L2().lPick,
                onNew: (opt) => { def.options = (def.options || []).concat([opt]); },
                onChange: (labels) => setCellValue(row, cell, def, multi ? (labels.length ? JSON.stringify(labels) : '') : (labels[0] || '')),
            });
            return;
        }
        if (def.type === 'status') {
            // 선택과 같은 알약 피커, 옵션은 그룹 순(할 일 → 진행 중 → 완료); 새 라벨은 서버가 할 일 그룹에 넣는다
            const rank = { todo: 0, doing: 1, done: 2 };
            const opts = (def.options || []).slice().sort((a, b) => (rank[a.group || 'todo'] || 0) - (rank[b.group || 'todo'] || 0));
            openPillPicker(cell, {
                options: opts, multi: false, allowNew: true, values: value ? [value] : [],
                noneLabel: L2().lNone, newLabel: L2().lNewOption, placeholder: L2().lPick,
                onNew: (opt) => { def.options = (def.options || []).concat([Object.assign({ group: 'todo' }, opt)]); },
                onChange: (labels) => setCellValue(row, cell, def, labels[0] || ''),
            });
            return;
        }
        if (def.type === 'users') {
            openPeoplePicker(cell, { multi: true, allowMe: false, values: parseMulti(value), onChange: (ids) => { const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean); setCellValue(row, cell, def, list.length ? JSON.stringify(list) : ''); } });
            return;
        }
        if (def.type === 'relation') {
            // 관계 셀 (AY9): 고르는 동안은 칩만 낙관적으로 바꾸고, 피커를 닫을 때 한 번만 patch 한다
            // (전에는 고를 때마다 patch + 새로고침 — N개를 고르면 N번). 바뀐 게 없으면 보내지 않는다.
            let cv = {}; try { cv = (JSON.parse(row.dataset.computed || '{}') || {})[def.id] || {}; } catch (_) { cv = {}; }
            const items = (cv.ids || []).map((id, i) => ({ id, title: (cv.titles || [])[i] || '…', color: (cv.colors || [])[i] || '' }));
            const beforeIds = JSON.stringify(items.map((x) => x.id));
            const target = (def.config && def.config.target) || '';
            const scope = target === 'me' ? 'me' : (target || calId);
            openEventPicker(cell, {
                scope, multi: !(def.config && def.config.single), values: items, exclude: [row.dataset.id],
                labels: { search: L2().lPickEvents, empty: L2().lNoMatch },
                onChange: (list) => paintRelationChips(cell, list),
                onClose: (list) => {
                    const afterIds = JSON.stringify(list.map((x) => x.id));
                    if (afterIds === beforeIds) return;
                    const before = {}; before['prop:' + def.id] = beforeIds;
                    const after = {}; after['prop:' + def.id] = afterIds;
                    commitQuiet(patchCommand(row.dataset.id, before, after), { refresh: 'now' });
                },
            });
            return;
        }
        if (def.type === 'files' || def.type === 'rollup' || def.type === 'autonumber' || def.type === 'formula') {
            if (window.openCalendarEvent) window.openCalendarEvent(row); // 파일 올리기·계산값은 패널에서
            return;
        }
        if (def.type === 'user') {
            const users = usersList();
            const cur = users.find((x) => x.value === value);
            openPillPicker(cell, {
                options: users.map((u) => ({ label: u.label, color: '' })), neutral: true, values: cur ? [cur.label] : [],
                noneLabel: L2().lNone, placeholder: L2().lPick,
                onChange: (labels) => { const u = users.find((x) => x.label === labels[0]); setCellValue(row, cell, def, u ? u.value : ''); },
            });
            return;
        }
        if (def.type === 'date') {
            const wrap = document.createElement('span');
            wrap.className = 'att-dp';
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.value = value;
            const trigger = document.createElement('button');
            trigger.type = 'button';
            trigger.className = 'att-dp-btn';
            trigger.style.cssText = 'position:absolute; width:0; height:0; padding:0; border:0; opacity:0;';
            wrap.appendChild(hidden);
            wrap.appendChild(trigger);
            cell.appendChild(wrap);
            const pop = document.getElementById('calpDatesPop');
            attachDatePicker(trigger, hidden, { weekdays: pop ? pop.dataset.weekdays : '', placeholder: '', onSet: (v) => { wrap.remove(); setCellValue(row, cell, def, v); } });
            trigger.click();
            const cleanup = () => { setTimeout(() => { if (!document.querySelector('.att-dp-pop') && wrap.parentNode) wrap.remove(); }, 0); document.removeEventListener('mousedown', cleanup, true); };
            setTimeout(() => document.addEventListener('mousedown', cleanup, true), 0);
            return;
        }
        // text / number / url: 인라인 입력
        if (cell.querySelector('input.calp-prop-edit')) return;
        const inp = document.createElement('input');
        inp.type = def.type === 'number' ? 'number' : 'text';
        inp.className = 'calp-prop-edit';
        inp.value = value;
        inp.maxLength = 500;
        cell.innerHTML = '';
        cell.appendChild(inp);
        inp.focus();
        inp.select();
        let done = false;
        const finish = (save) => {
            if (done) return;
            done = true;
            const v = inp.value.trim();
            paintCell(cell, def, save ? v : value);
            if (save) setCellValue(row, { dataset: { value } , innerHTML: '', appendChild: () => {} }, def, v) ; // placeholder replaced below
        };
        // 위 finish 의 setCellValue 는 셀 참조가 필요하므로 실제 구현으로 대체
        const finishReal = (save) => {
            if (done) return;
            done = true;
            const v = inp.value.trim();
            if (!save || v === value) { paintCell(cell, def, value); return; }
            paintCell(cell, def, value); // dataset.value 원복 뒤 setCellValue 가 before 를 읽는다
            setCellValue(row, cell, def, v);
        };
        void finish;
        inp.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') { e.preventDefault(); finishReal(true); row.focus(); }
            else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); finishReal(false); row.focus(); }
            else if (e.key === 'Tab') { e.preventDefault(); finishReal(true); const cells = Array.from(row.querySelectorAll('.calp-td-prop')); const i = cells.indexOf(cell); const next = cells[i + (e.shiftKey ? -1 : 1)]; if (next) editCell(row, next); }
        });
        inp.addEventListener('blur', () => finishReal(true));
        ['mousedown', 'click'].forEach((t) => inp.addEventListener(t, (e) => e.stopPropagation()));
    }
    hub.addEventListener('click', (e) => {
        const cell = e.target.closest('.calp-td-prop');
        if (!cell) return;
        const row = cell.closest('.calp-tr[data-id]');
        if (!row) return;
        if (e.target.closest('a')) return;
        if (e.ctrlKey || e.metaKey || e.shiftKey) return; // 다중 선택 클릭은 그대로
        e.stopPropagation();
        e.preventDefault();
        if (e.target.closest('input.calp-prop-edit')) return;
        editCell(row, cell);
    }, true);
    // 체크박스는 change 로도 들어온다 (클릭 캡처에서 이미 토글하므로 여기선 기본 동작만 막는다)
    hub.addEventListener('change', (e) => {
        if (e.target.closest('.calp-prop-check')) e.stopPropagation();
    }, true);
    // 우클릭: 선택형 셀 값으로 필터
    hub.addEventListener('contextmenu', (e) => {
        const cell = e.target.closest('.calp-td-prop');
        if (!cell) return;
        const def = defOf(cell.dataset.prop);
        if (!def || (def.type !== 'select' && def.type !== 'multiselect' && def.type !== 'user' && def.type !== 'check')) return;
        const pillEl = e.target.closest('.prop-pill');
        let value = cell.dataset.value || '';
        if (def.type === 'multiselect') value = pillEl ? pillEl.textContent : (parseMulti(value)[0] || '');
        if (!value) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        const label = ((modalEl && modalEl.dataset.lFilterBy) || "'%s' 값만 보기").replace('%s', def.type === 'user' ? ((usersList().find((u) => u.value === value) || {}).label || value) : (def.type === 'check' ? '✓' : value));
        showContextMenu(e.clientX, e.clientY, [
            { label, onClick: () => { const u = new URL(window.location.href); ['pf', 'pv', 'flt', 'm'].forEach((k) => u.searchParams.delete(k)); u.searchParams.set('flt', 'prop:' + def.id + '~' + (def.type === 'multiselect' ? 'has' : 'is') + '~' + value); navigate(u.pathname + '?' + u.searchParams.toString(), { keepScroll: true }); } },
        ]);
    }, true);

    // ── 일괄 속성 적용 (선택된 행) ──
    function showBulkProp() {
        const list = targets();
        if (!list.length || !schema.length) return;
        const old = document.getElementById('calpBulkProp');
        if (old) old.remove();
        const box = document.createElement('div');
        box.id = 'calpBulkProp';
        box.className = 'calp-bulkprop';
        const sel = document.createElement('select');
        schema.forEach((d) => { const o = document.createElement('option'); o.value = d.id; o.textContent = d.name; sel.appendChild(o); });
        sel.setAttribute('data-inapp-select', '');
        const valWrap = document.createElement('span');
        const close = () => box.remove();
        function renderValue() {
            valWrap.innerHTML = '';
            const def = defOf(sel.value);
            if (!def) return;
            const fake = { dataset: { value: '' } };
            const apply = (v) => {
                close();
                const pa = {}; pa['prop:' + def.id] = v;
                splitScope(list, 'edit').then((sp) => { // 반복 인스턴스가 섞이면 한 번 묻는다 (BA1)
                    if (!sp) return;
                    const cmds = sp.plain.map((r) => {
                        const cell = r.querySelector('.calp-td-prop[data-prop="' + CSS.escape(def.id) + '"]');
                        const before = cell ? cell.dataset.value || '' : '';
                        if (cell) paintCell(cell, def, v);
                        const pb = {}; pb['prop:' + def.id] = before;
                        return patchCommand(r.dataset.id, pb, pa);
                    });
                    if (cmds.length) commitQuiet(composite(cmds), { refresh: 'now' });
                    if (sp.scoped.length) sendScoped(sp.scoped, sp.scope, () => pa);
                });
            };
            void fake;
            if (def.type === 'check') {
                const b1 = document.createElement('button'); b1.type = 'button'; b1.className = 'btn btn-secondary btn-sm'; b1.textContent = '✓'; b1.addEventListener('click', () => apply('1'));
                const b0 = document.createElement('button'); b0.type = 'button'; b0.className = 'btn btn-secondary btn-sm'; b0.textContent = '–'; b0.addEventListener('click', () => apply(''));
                valWrap.appendChild(b1); valWrap.appendChild(b0);
                return;
            }
            if (def.type === 'select' || def.type === 'multiselect' || def.type === 'user') {
                const b = document.createElement('button'); b.type = 'button'; b.className = 'btn btn-secondary btn-sm'; b.textContent = L2().lPick || '선택…';
                b.addEventListener('click', () => {
                    if (def.type === 'user') {
                        const users = usersList();
                        openPillPicker(b, { options: users.map((u) => ({ label: u.label, color: '' })), neutral: true, values: [], noneLabel: L2().lNone, onChange: (labels) => { const u = users.find((x) => x.label === labels[0]); apply(u ? u.value : ''); } });
                        return;
                    }
                    const multi = def.type === 'multiselect';
                    openPillPicker(b, { options: def.options || [], multi, allowNew: true, values: [], newLabel: L2().lNewOption, noneLabel: L2().lNone,
                        onNew: (opt) => { def.options = (def.options || []).concat([opt]); },
                        onChange: (labels) => { if (!multi) apply(labels[0] || ''); else valWrap.dataset.pending = JSON.stringify(labels); } });
                });
                valWrap.appendChild(b);
                if (def.type === 'multiselect') { const ok = document.createElement('button'); ok.type = 'button'; ok.className = 'btn btn-primary btn-sm'; ok.textContent = (document.getElementById('calpBulkDatesApply') || {}).textContent || '적용'; ok.addEventListener('click', () => apply(valWrap.dataset.pending || '')); valWrap.appendChild(ok); }
                return;
            }
            const inp = document.createElement('input'); inp.type = def.type === 'number' ? 'number' : 'text'; inp.className = 'form-input'; inp.style.height = '30px'; inp.placeholder = def.type === 'date' ? 'YYYY-MM-DD' : '';
            const ok = document.createElement('button'); ok.type = 'button'; ok.className = 'btn btn-primary btn-sm'; ok.textContent = (document.getElementById('calpBulkDatesApply') || {}).textContent || '적용'; ok.addEventListener('click', () => apply(inp.value.trim()));
            inp.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); apply(inp.value.trim()); } });
            valWrap.appendChild(inp); valWrap.appendChild(ok);
            setTimeout(() => inp.focus(), 0);
        }
        sel.addEventListener('change', renderValue);
        const x = document.createElement('button'); x.type = 'button'; x.className = 'cal-icon-btn'; x.textContent = '✕'; x.addEventListener('click', close);
        box.appendChild(sel); box.appendChild(valWrap); box.appendChild(x);
        document.body.appendChild(box);
        initInappSelects(box);
        renderValue();
        ['mousedown', 'click'].forEach((t) => box.addEventListener(t, (e) => e.stopPropagation()));
    }

    // ── 클릭 (캡처: project-timeline 의 행 클릭 위임보다 먼저) ──
    hub.addEventListener('click', (e) => {
        // 그룹 머리줄 「＋」: 그 그룹의 마지막 행 뒤에(비어 있으면 표 끝에) 그룹 값이 채워진 새 작업
        const gadd = e.target.closest('.cal-group-add');
        if (gadd) {
            e.stopPropagation(); e.preventDefault();
            let prefill = {};
            try { prefill = JSON.parse(gadd.dataset.prefill || '{}'); } catch (_) { prefill = {}; }
            const head = gadd.closest('.cal-group-head');
            let last = head;
            let n = head.nextElementSibling;
            while (n && n.classList.contains('calp-tr')) { last = n; n = n.nextElementSibling; }
            createSiblingAfter(last === head ? null : last.dataset.id, false, last === head ? null : last, prefill);
            return;
        }
        const row = e.target.closest('.calp-tr[data-id]');
        if (!row) return;
        if (e.target.closest('.calp-fold')) { e.stopPropagation(); e.preventDefault(); toggleFold(row.dataset.id); return; }
        if (e.target.closest('.calp-open')) { e.stopPropagation(); e.preventDefault(); if (window.openCalendarEvent) window.openCalendarEvent(row); return; }
        const modifier = e.ctrlKey || e.metaKey || e.shiftKey;
        // Ctrl/Shift+클릭은 행 어디서나(속성 셀·기간 트랙·상태 배지 위에서도) 선택이다 — 뷰마다 열 순서가
        // 달라 행 가운데가 어느 셀인지 정해져 있지 않다. 체크박스·그립만 언제나 제 일을 한다.
        if (e.target.closest('.calp-sel, .calp-grip')) return;
        if (!modifier && e.target.closest('.calp-done, .calp-track, .calp-status, .calp-td-prop')) return;
        if (modifier) {
            e.stopPropagation();
            e.preventDefault();
            if (e.shiftKey && anchorId) selectRange(anchorId, row.dataset.id);
            else {
                if (selected.has(row.dataset.id)) selected.delete(row.dataset.id);
                else selected.add(row.dataset.id);
                anchorId = row.dataset.id;
            }
            applySelection();
            return;
        }
        if (e.target.closest('input.calp-edit')) { e.stopPropagation(); return; }
        if (e.target.closest('.calp-td-text')) { e.stopPropagation(); e.preventDefault(); focusedId = row.dataset.id; startEdit(row); return; }
        if (selected.size) { e.stopPropagation(); e.preventDefault(); clearSelection(); }
    }, true);
    // 선택 체크박스(행) / 전체 선택(헤더)
    hub.addEventListener('change', (e) => {
        const all = e.target.closest('#calpSelAll');
        if (all) {
            selected.clear();
            if (all.checked) rows().forEach((r) => selected.add(r.dataset.id));
            applySelection();
            return;
        }
        const cb = e.target.closest('.calp-sel');
        if (!cb) return;
        const row = cb.closest('.calp-tr[data-id]');
        if (!row) return;
        if (cb.checked) { selected.add(row.dataset.id); anchorId = row.dataset.id; } else selected.delete(row.dataset.id);
        applySelection();
    });
    // 체크 링(.calp-done) = 완료 토글 — 표 행만; 타임라인 행(.calp-tl)은 project-timeline.js 가 맡는다 (2026-09-18).
    hub.addEventListener('change', (e) => {
        const ring = e.target.closest('.calp-done');
        if (!ring || ring.closest('.calp-tl')) return;
        const row = ring.closest('.calp-tr[data-id]');
        if (!row) return;
        toggleDone(selected.has(row.dataset.id) ? targets() : [row]);
    });
    if (selBar) {
        selBar.addEventListener('click', (e) => {
            const btn = e.target.closest('button');
            if (!btn) return;
            const list = targets();
            if (btn.dataset.color) setColor(list, btn.dataset.color);
            else if (btn.dataset.act === 'done') toggleDone(list);
            else if (btn.dataset.act === 'shift-prev') shiftDates(list, -1);
            else if (btn.dataset.act === 'shift-next') shiftDates(list, 1);
            else if (btn.dataset.act === 'dates') showDatesPop();
            else if (btn.dataset.act === 'prop') showBulkProp();
            else if (btn.dataset.act === 'indent') indent(list);
            else if (btn.dataset.act === 'outdent') outdent(list);
            else if (btn.dataset.act === 'dup') duplicate(list);
            else if (btn.dataset.act === 'delete') remove(list);
            else if (btn.dataset.act === 'clear') clearSelection();
        });
    }

    // ── 키보드 ──
    document.addEventListener('keydown', (e) => {
        if (overlayOpen() || inTextField(e.target)) return;
        const row = focusedRow();
        const mod = e.ctrlKey || e.metaKey;
        if (e.key === 'Escape') {
            if (selected.size) { clearSelection(); e.preventDefault(); }
            return;
        }
        if (mod && e.key.toLowerCase() === 'a' && rows().length) {
            e.preventDefault();
            rows().forEach((r) => selected.add(r.dataset.id));
            applySelection();
            return;
        }
        if (!row) return;
        const list = targets();
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            if (e.altKey) { move(row, e.key === 'ArrowUp' ? -1 : 1); return; }
            const vis = visibleRows();
            const i = vis.indexOf(row);
            const next = vis[i + (e.key === 'ArrowDown' ? 1 : -1)];
            if (next) {
                if (e.shiftKey) { selected.add(row.dataset.id); selected.add(next.dataset.id); applySelection(); }
                focusRow(next);
            }
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (mod) { if (window.openCalendarEvent) window.openCalendarEvent(row); }
            else startEdit(row);
        } else if (e.key === ' ') {
            e.preventDefault();
            toggleDone(list);
        } else if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            remove(list);
        } else if (e.key === 'Tab') {
            e.preventDefault();
            if (e.shiftKey) outdent(list); else indent(list);
        } else if (e.altKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
            e.preventDefault();
            shiftDates(list, e.key === 'ArrowLeft' ? -1 : 1);
        } else if (mod && e.key.toLowerCase() === 'd') {
            e.preventDefault();
            duplicate(list);
        }
    });
}
